/**
 * EffectLayer.ts - PixiJS overlay for staging equipment visual effects
 *
 * Renders particle/animation effects on a transparent canvas that sits
 * above the building view. Each staging prop type produces a distinct
 * visual effect inside its room's bounding box.
 *
 * Uses PixiJS v8 Application API with a proper particle pool system.
 */

import { Application, Container, Graphics } from 'pixi.js';
import type { PropId } from '../game/data/props.js';

// ── Types ──────────────────────────────────────────────────

export interface RoomEffectInfo {
  floorLevel: number;
  propIds: PropId[];
  rect: { x: number; y: number; width: number; height: number };
}

/** Individual particle with physics and lifecycle */
interface Particle {
  graphic: Graphics;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  baseAlpha: number;
  color: number;
  /** Per-particle random seed for varied motion */
  seed: number;
  /** Particle-specific extra state */
  angle: number;
  angularVel: number;
  /** For snowflake/star shaped particles */
  shape: 'circle' | 'star' | 'rect' | 'ring';
}

interface ActiveEffect {
  floorLevel: number;
  propId: PropId;
  container: Container;
  elapsed: number;
  particles: Particle[];
  /** Room dimensions (updated every frame via sync) */
  roomW: number;
  roomH: number;
  phase: number;
  scareActive: boolean;
  scareTimer: number;
  /** Effect-specific state storage */
  state: Record<string, number>;
}

// ── Staging prop IDs that have visual effects ──────────────

// Props handled by CSS instead of PixiJS (fog/mist look better in CSS):
// dry_ice, scent_sprayer, strobe_light, temp_controller

const EFFECT_PROP_IDS: PropId[] = [
  'surround_speaker',
  'vibration_floor',
  'hologram_device',
  'auto_lock',
];

// ── Color palettes per effect ──────────────────────────────

const COLORS: Record<string, number[]> = {
  dry_ice: [0xffffff, 0xe0d0ff, 0xc8a8ff, 0xd0e0ff, 0xb8c8ff],
  strobe_light: [0xffffff, 0xffff99, 0xffffcc],
  surround_speaker: [0x9b59b6, 0xc084fc, 0x7c3aed, 0xa855f7],
  vibration_floor: [0xf97316, 0xfbbf24, 0xef4444, 0xff6b35],
  temp_controller: [0x3498db, 0x93c5fd, 0xbfdbfe, 0xe0f0ff, 0x60a5fa],
  hologram_device: [0x2ecc71, 0x34d399, 0x6ee7b7, 0x10b981],
  scent_sprayer: [0x9b59b6, 0x2ecc71, 0xa78bfa, 0x8b5cf6, 0x34d399],
  auto_lock: [0x1a1a2e, 0x0d0d1a, 0x2a2a4a],
};

// ── Utility ────────────────────────────────────────────────

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** Smooth easing: ease-out cubic */
function easeOut(t: number): number {
  const t1 = 1 - t;
  return 1 - t1 * t1 * t1;
}

/** Smooth easing: ease-in-out sine */
function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * t) - 1) / 2;
}

/** Lerp */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// ── Particle creation helpers ──────────────────────────────

function createParticle(container: Container, overrides: Partial<Particle> = {}): Particle {
  const g = new Graphics();
  container.addChild(g);
  return {
    graphic: g,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    life: 1,
    maxLife: 1,
    size: 4,
    baseAlpha: 0.5,
    color: 0xffffff,
    seed: Math.random() * 1000,
    angle: 0,
    angularVel: 0,
    shape: 'circle',
    ...overrides,
  };
}

function resetParticle(p: Particle, overrides: Partial<Particle> = {}): void {
  p.life = overrides.life ?? p.maxLife;
  p.x = overrides.x ?? 0;
  p.y = overrides.y ?? 0;
  p.vx = overrides.vx ?? 0;
  p.vy = overrides.vy ?? 0;
  p.size = overrides.size ?? p.size;
  p.baseAlpha = overrides.baseAlpha ?? p.baseAlpha;
  p.color = overrides.color ?? p.color;
  p.angle = overrides.angle ?? p.angle;
  p.angularVel = overrides.angularVel ?? p.angularVel;
  p.shape = overrides.shape ?? p.shape;
  p.seed = Math.random() * 1000;
}

/** Draw a particle's shape on its Graphics object */
function drawParticle(p: Particle): void {
  const g = p.graphic;
  g.clear();

  const lifeRatio = Math.max(0, p.life / p.maxLife);
  const alpha = p.baseAlpha * lifeRatio;
  if (alpha <= 0.001) {
    g.visible = false;
    return;
  }
  g.visible = true;

  switch (p.shape) {
    case 'circle': {
      // Multi-layered soft circle for fog/mist feel
      g.circle(0, 0, p.size);
      g.fill({ color: p.color, alpha });
      // Outer soft glow layer
      g.circle(0, 0, p.size * 1.5);
      g.fill({ color: p.color, alpha: alpha * 0.25 });
      break;
    }
    case 'star': {
      // 6-pointed snowflake/star
      const s = p.size;
      const innerR = s * 0.4;
      const points = 6;
      for (let i = 0; i < points; i++) {
        const a1 = (i / points) * Math.PI * 2 + p.angle;
        const a2 = ((i + 0.5) / points) * Math.PI * 2 + p.angle;
        const ox = Math.cos(a1) * s;
        const oy = Math.sin(a1) * s;
        const ix = Math.cos(a2) * innerR;
        const iy = Math.sin(a2) * innerR;
        // Draw each spoke as a thin line
        g.moveTo(0, 0);
        g.lineTo(ox, oy);
        g.stroke({ color: p.color, alpha, width: 1 });
        g.circle(ox, oy, 1);
        g.fill({ color: p.color, alpha });
        // Small branch at inner point
        g.moveTo(ix, iy);
        g.lineTo(ix + Math.cos(a1) * innerR * 0.5, iy + Math.sin(a1) * innerR * 0.5);
        g.stroke({ color: p.color, alpha: alpha * 0.6, width: 0.5 });
      }
      // Center dot
      g.circle(0, 0, 1);
      g.fill({ color: p.color, alpha });
      break;
    }
    case 'rect': {
      g.rect(-p.size / 2, -p.size / 2, p.size, p.size);
      g.fill({ color: p.color, alpha });
      break;
    }
    case 'ring': {
      g.circle(0, 0, p.size);
      g.stroke({ color: p.color, alpha, width: 1.5 });
      break;
    }
  }

  g.x = p.x;
  g.y = p.y;
  g.rotation = p.shape === 'star' ? p.angle : 0;
}

// ── EffectLayer Class ──────────────────────────────────────

export class EffectLayer {
  private app: Application | null = null;
  private effects: Map<string, ActiveEffect> = new Map();
  private initialized = false;
  private tickerCallback: ((ticker: { deltaMS: number }) => void) | null = null;

  /**
   * Initialize the PixiJS application on the given canvas.
   * Canvas should be positioned absolutely over the building container
   * with pointer-events: none.
   */
  async init(canvas: HTMLCanvasElement): Promise<void> {
    if (this.initialized) return;

    this.app = new Application();

    await this.app.init({
      canvas,
      backgroundAlpha: 0,
      resizeTo: canvas,
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
    });

    this.tickerCallback = (ticker) => {
      this.update(ticker.deltaMS);
    };
    this.app.ticker.add(this.tickerCallback);
    this.initialized = true;
  }

  /** Check if the layer has been initialized */
  get isReady(): boolean {
    return this.initialized && this.app !== null;
  }

  /**
   * Sync effects with the current room layout.
   * Call this every render frame from the main game loop.
   */
  syncRoomEffects(rooms: RoomEffectInfo[], canvasRect: DOMRect, isOpen = false, visitorFloorLevel = -1): void {
    if (!this.app) return;

    const activeKeys = new Set<string>();

    for (const room of rooms) {
      const stagingProps = room.propIds.filter((id) => EFFECT_PROP_IDS.includes(id));

      for (const propId of stagingProps) {
        const key = `${room.floorLevel}-${propId}`;
        activeKeys.add(key);

        let effect = this.effects.get(key);
        if (!effect) {
          const container = new Container();
          this.app.stage.addChild(container);
          effect = {
            floorLevel: room.floorLevel,
            propId,
            container,
            elapsed: 0,
            particles: [],
            roomW: room.rect.width,
            roomH: room.rect.height,
            phase: Math.random() * Math.PI * 2,
            scareActive: false,
            scareTimer: 0,
            state: {},
          };
          this.effects.set(key, effect);
          this.initEffect(effect);
        }

        // Update position and dimensions
        effect.container.x = room.rect.x - canvasRect.x;
        effect.container.y = room.rect.y - canvasRect.y;
        effect.roomW = room.rect.width;
        effect.roomH = room.rect.height;

        // Visibility: open+no visitor = hidden, open+visitor = show, closed = intermittent
        if (isOpen) {
          effect.container.visible = (room.floorLevel === visitorFloorLevel);
        } else {
          // Closed: pulse on/off (3s on, 3s off)
          const cycle = (effect.elapsed % 6000);
          effect.container.visible = cycle < 3000;
        }

        // Clip: hide particles that escape room bounds
        for (const p of (effect.particles || [])) {
          if (p.x < -10 || p.x > room.rect.width + 10 ||
              p.y < -10 || p.y > room.rect.height + 10) {
            p.graphic.visible = false;
          }
        }
      }
    }

    // Remove effects that no longer have corresponding rooms
    for (const [key, effect] of this.effects) {
      if (!activeKeys.has(key)) {
        this.app.stage.removeChild(effect.container);
        effect.container.destroy({ children: true });
        this.effects.delete(key);
      }
    }
  }

  /**
   * Trigger scare enhancement when visitors arrive at a floor.
   */
  triggerScareEffects(floorLevel: number): void {
    for (const [_key, effect] of this.effects) {
      if (effect.floorLevel === floorLevel) {
        effect.scareActive = true;
        effect.scareTimer = 2000; // 2 seconds of enhanced effects
      }
    }
  }

  /**
   * Clean up all PixiJS resources.
   */
  destroy(): void {
    if (this.app && this.tickerCallback) {
      this.app.ticker.remove(this.tickerCallback);
    }
    this.effects.clear();
    if (this.app) {
      this.app.destroy(true);
      this.app = null;
    }
    this.initialized = false;
  }

  // ── Private: Effect Initialization ─────────────────────

  private initEffect(effect: ActiveEffect): void {
    const count = this.getParticleCount(effect.propId);
    const colors = COLORS[effect.propId] ?? [0xffffff];

    for (let i = 0; i < count; i++) {
      const p = createParticle(effect.container, {
        color: pick(colors),
        seed: Math.random() * 1000,
      });
      effect.particles.push(p);
    }

    // Initialize per-effect starting positions
    this.spawnAllParticles(effect);
  }

  private getParticleCount(propId: PropId): number {
    switch (propId) {
      case 'dry_ice': return 25;
      case 'strobe_light': return 3; // flash layers
      case 'surround_speaker': return 6; // concentric rings
      case 'vibration_floor': return 16;
      case 'temp_controller': return 18;
      case 'hologram_device': return 12; // scanlines + ghost parts
      case 'auto_lock': return 2;
      case 'scent_sprayer': return 14;
      default: return 0;
    }
  }

  /** Set initial spawn positions for all particles in an effect */
  private spawnAllParticles(effect: ActiveEffect): void {
    const w = effect.roomW || 160;
    const h = effect.roomH || 100;

    switch (effect.propId) {
      case 'dry_ice':
        for (const p of effect.particles) {
          this.spawnDryIceParticle(p, w, h);
        }
        break;
      case 'strobe_light':
        // Flash layers initialized in update
        break;
      case 'surround_speaker':
        for (let i = 0; i < effect.particles.length; i++) {
          this.spawnSpeakerRing(effect.particles[i], i, effect.particles.length);
        }
        break;
      case 'vibration_floor':
        for (const p of effect.particles) {
          this.spawnVibrationParticle(p, w, h);
        }
        break;
      case 'temp_controller':
        for (const p of effect.particles) {
          this.spawnTempParticle(p, w, h);
        }
        break;
      case 'hologram_device':
        // Initialized in update
        break;
      case 'auto_lock':
        // Doors - no spawn needed
        break;
      case 'scent_sprayer':
        for (const p of effect.particles) {
          this.spawnScentParticle(p, w, h);
        }
        break;
    }
  }

  // ── Particle Spawners (for pool recycling) ─────────────

  private spawnDryIceParticle(p: Particle, w: number, h: number): void {
    const colors = COLORS.dry_ice;
    resetParticle(p, {
      x: rand(w * -0.1, w * 1.1),
      y: h * rand(0.55, 1.0),
      vx: rand(-0.3, 0.3),
      vy: rand(-0.08, 0.04),
      size: rand(25, 55),
      baseAlpha: rand(0.12, 0.35),
      color: pick(colors),
      life: rand(4000, 8000),
      shape: 'ellipse' as any,
    });
    p.maxLife = p.life;
  }

  private spawnSpeakerRing(p: Particle, index: number, total: number): void {
    const colors = COLORS.surround_speaker;
    const stagger = (index / total);
    resetParticle(p, {
      x: 0, y: 0,
      vx: 0, vy: 0,
      size: 5 + stagger * 30,
      baseAlpha: 0.4,
      color: pick(colors),
      life: 2000,
      shape: 'ring',
    });
    p.maxLife = 2000;
    p.seed = stagger * 2000; // time offset
  }

  private spawnVibrationParticle(p: Particle, w: number, h: number): void {
    const colors = COLORS.vibration_floor;
    resetParticle(p, {
      x: rand(w * 0.05, w * 0.95),
      y: h - rand(0, 4),
      vx: rand(-0.3, 0.3),
      vy: rand(-1.5, -0.5),
      size: rand(2, 5),
      baseAlpha: rand(0.4, 0.8),
      color: pick(colors),
      life: rand(600, 1200),
      shape: 'rect',
    });
    p.maxLife = p.life;
  }

  private spawnTempParticle(p: Particle, _w: number, h: number): void {
    const colors = COLORS.temp_controller;
    const isStar = Math.random() > 0.5;
    resetParticle(p, {
      x: -rand(5, 20),
      y: rand(h * 0.1, h * 0.9),
      vx: rand(0.5, 1.5),
      vy: rand(-0.1, 0.1),
      size: isStar ? rand(3, 6) : rand(2, 5),
      baseAlpha: rand(0.2, 0.5),
      color: pick(colors),
      life: rand(2500, 5000),
      shape: isStar ? 'star' : 'circle',
      angle: rand(0, Math.PI * 2),
      angularVel: isStar ? rand(-0.005, 0.005) : 0,
    });
    p.maxLife = p.life;
  }

  private spawnScentParticle(p: Particle, w: number, h: number): void {
    const colors = COLORS.scent_sprayer;
    const cx = w * 0.5;
    resetParticle(p, {
      x: cx + rand(-10, 10),
      y: h - rand(0, 5),
      vx: rand(-0.08, 0.08),
      vy: rand(-0.5, -0.2),
      size: rand(2, 5),
      baseAlpha: rand(0.2, 0.5),
      color: pick(colors),
      life: rand(2000, 4000),
      shape: 'circle',
      angle: rand(0, Math.PI * 2),
      angularVel: rand(0.01, 0.03),
    });
    p.maxLife = p.life;
  }

  // ── Private: Per-Frame Update ──────────────────────────

  private update(deltaMs: number): void {
    for (const [_key, effect] of this.effects) {
      effect.elapsed += deltaMs;

      // Decay scare timer
      if (effect.scareActive) {
        effect.scareTimer -= deltaMs;
        if (effect.scareTimer <= 0) {
          effect.scareActive = false;
          effect.scareTimer = 0;
        }
      }

      switch (effect.propId) {
        case 'dry_ice':
          this.updateDryIce(effect, deltaMs);
          break;
        case 'strobe_light':
          this.updateStrobeLight(effect, deltaMs);
          break;
        case 'surround_speaker':
          this.updateSurroundSpeaker(effect, deltaMs);
          break;
        case 'vibration_floor':
          this.updateVibrationFloor(effect, deltaMs);
          break;
        case 'temp_controller':
          this.updateTempController(effect, deltaMs);
          break;
        case 'hologram_device':
          this.updateHologram(effect, deltaMs);
          break;
        case 'auto_lock':
          this.updateAutoLock(effect, deltaMs);
          break;
        case 'scent_sprayer':
          this.updateScentSprayer(effect, deltaMs);
          break;
      }
    }
  }

  // ── Effect Renderers ───────────────────────────────────

  /**
   * Dry Ice: Low-hanging fog that blankets the floor.
   * Wide, flat ellipses that drift slowly along the ground,
   * layered for depth. Not rising circles — ground-hugging mist.
   * On scare: thicker and brighter fog.
   */
  private updateDryIce(effect: ActiveEffect, dt: number): void {
    const w = effect.roomW;
    const h = effect.roomH;
    const scare = effect.scareActive;
    const intensityMul = scare ? 1.8 : 1.0;

    for (const p of effect.particles) {
      p.life -= dt;
      if (p.life <= 0) {
        this.spawnDryIceParticle(p, w, h);
      }

      const lifeRatio = p.life / p.maxLife;

      // Slow lateral drift with gentle wave
      const drift = Math.sin(effect.elapsed * 0.0005 + p.seed) * 8
                  + Math.sin(effect.elapsed * 0.0012 + p.seed * 2.1) * 4;

      p.x += p.vx * dt * 0.03 + drift * 0.008;
      p.y += p.vy * dt * 0.02;

      // Fog stays near ground — if drifts too high, pull back down
      const groundLevel = h * 0.55;
      if (p.y < groundLevel) {
        p.y += (groundLevel - p.y) * 0.01;
      }

      // Pulse size slowly
      const pulse = 1 + Math.sin(effect.elapsed * 0.0008 + p.seed * 3) * 0.15;
      const currentW = p.size * 2.5 * pulse;  // wide
      const currentH = p.size * 0.5 * pulse;  // flat

      // Fade: strongest in middle of life
      const fadeCurve = Math.sin(lifeRatio * Math.PI);
      const fadeAlpha = fadeCurve * p.baseAlpha * intensityMul;

      const g = p.graphic;
      g.clear();
      if (fadeAlpha > 0.005) {
        g.visible = true;
        // Inner dense fog core (wide ellipse)
        g.ellipse(0, 0, currentW * 0.5, currentH * 0.6);
        g.fill({ color: p.color, alpha: fadeAlpha });
        // Mid fog layer
        g.ellipse(0, 0, currentW * 0.8, currentH);
        g.fill({ color: p.color, alpha: fadeAlpha * 0.35 });
        // Outer diffuse glow
        g.ellipse(0, 0, currentW, currentH * 1.4);
        g.fill({ color: p.color, alpha: fadeAlpha * 0.1 });
      } else {
        g.visible = false;
      }
      g.x = p.x;
      g.y = p.y;
    }
  }

  /**
   * Strobe Light: Room-wide flash that blinks at random intervals.
   * On scare: rapid continuous strobe.
   */
  private updateStrobeLight(effect: ActiveEffect, dt: number): void {
    const w = effect.roomW;
    const h = effect.roomH;
    const scare = effect.scareActive;

    // Initialize state
    if (effect.state['nextFlash'] === undefined) {
      effect.state['nextFlash'] = rand(300, 800);
      effect.state['flashTimer'] = 0;
      effect.state['flashDuration'] = 0;
      effect.state['flashAlpha'] = 0;
    }

    effect.state['flashTimer'] += dt;

    // Flash scheduling
    if (scare) {
      // Rapid strobe: alternate every 80-150ms
      const strobeInterval = 80 + Math.sin(effect.elapsed * 0.02) * 35;
      const strobePhase = (effect.elapsed % (strobeInterval * 2)) / (strobeInterval * 2);
      effect.state['flashAlpha'] = strobePhase < 0.5 ? 0.4 : 0;
    } else {
      // Normal: random flashes
      if (effect.state['flashTimer'] >= effect.state['nextFlash']) {
        effect.state['flashTimer'] = 0;
        effect.state['flashDuration'] = rand(40, 120);
        effect.state['nextFlash'] = rand(400, 1200);
        effect.state['flashAlpha'] = rand(0.1, 0.25);
      }

      // Decay flash
      if (effect.state['flashDuration'] > 0) {
        effect.state['flashDuration'] -= dt;
        if (effect.state['flashDuration'] <= 0) {
          effect.state['flashAlpha'] = 0;
        }
      }
    }

    const alpha = effect.state['flashAlpha'];

    // Layer 0: main flash
    const g0 = effect.particles[0]?.graphic;
    if (g0) {
      g0.clear();
      if (alpha > 0.005) {
        g0.visible = true;
        g0.rect(0, 0, w, h);
        g0.fill({ color: 0xffffff, alpha });
      } else {
        g0.visible = false;
      }
    }

    // Layer 1: warm tint flash (slightly delayed)
    const g1 = effect.particles[1]?.graphic;
    if (g1) {
      g1.clear();
      const warmAlpha = alpha * 0.4;
      if (warmAlpha > 0.005) {
        g1.visible = true;
        g1.rect(0, 0, w, h);
        g1.fill({ color: 0xffff99, alpha: warmAlpha });
      } else {
        g1.visible = false;
      }
    }

    // Layer 2: edge glow
    const g2 = effect.particles[2]?.graphic;
    if (g2) {
      g2.clear();
      const edgeAlpha = alpha * 0.6;
      if (edgeAlpha > 0.005) {
        g2.visible = true;
        // Bright center gradient feel: smaller bright rect in center
        const margin = w * 0.15;
        g2.rect(margin, h * 0.1, w - margin * 2, h * 0.8);
        g2.fill({ color: 0xffffff, alpha: edgeAlpha * 0.3 });
      } else {
        g2.visible = false;
      }
    }
  }

  /**
   * Surround Speaker: Concentric sound wave rings expanding from center.
   * 3-4 rings with staggered timing, fading as they expand.
   */
  private updateSurroundSpeaker(effect: ActiveEffect, _dt: number): void {
    const w = effect.roomW;
    const h = effect.roomH;
    const cx = w / 2;
    const cy = h / 2;
    const maxRadius = Math.min(w, h) * 0.65;
    const scare = effect.scareActive;
    const speed = scare ? 1.8 : 1.0;
    const cycleDuration = 2000 / speed;

    for (let i = 0; i < effect.particles.length; i++) {
      const p = effect.particles[i];
      const offset = (i / effect.particles.length) * cycleDuration;
      const t = ((effect.elapsed + offset) % cycleDuration) / cycleDuration;
      const expandedT = easeOut(t);

      const radius = 6 + expandedT * maxRadius;
      const alpha = (1 - expandedT) * (scare ? 0.5 : 0.35);
      const thickness = lerp(2.5, 0.5, expandedT);

      const g = p.graphic;
      g.clear();
      if (alpha > 0.005 && radius > 2) {
        g.visible = true;
        // Main ring
        g.circle(cx, cy, radius);
        g.stroke({ color: p.color, alpha, width: thickness });
        // Subtle inner echo ring
        if (radius > 15) {
          g.circle(cx, cy, radius * 0.85);
          g.stroke({ color: p.color, alpha: alpha * 0.25, width: thickness * 0.5 });
        }
      } else {
        g.visible = false;
      }
    }
  }

  /**
   * Vibration Floor: Small particles bounce up from the floor.
   * Container shakes, floor line wobbles.
   */
  private updateVibrationFloor(effect: ActiveEffect, dt: number): void {
    const w = effect.roomW;
    const h = effect.roomH;
    const scare = effect.scareActive;
    const shakeIntensity = scare ? 3.5 : 1.2;

    // Shake the container
    const shakeFreq = effect.elapsed * 0.03;
    effect.container.pivot.x = Math.sin(shakeFreq) * shakeIntensity;
    effect.container.pivot.y = Math.cos(shakeFreq * 1.3) * shakeIntensity * 0.5;

    // Reserve last 2 particles for floor line effect
    const floorParticles = effect.particles.slice(0, -2);
    const floorLines = effect.particles.slice(-2);

    // Update bouncing particles
    for (const p of floorParticles) {
      p.life -= dt;
      if (p.life <= 0) {
        this.spawnVibrationParticle(p, w, h);
        if (scare) {
          p.vy *= 1.5;
          p.baseAlpha = Math.min(1, p.baseAlpha * 1.3);
        }
      }

      // Physics: gravity pulls back down
      p.vy += 0.003 * dt; // gravity
      p.x += p.vx * dt * 0.05;
      p.y += p.vy * dt * 0.05;

      // Clamp to room
      if (p.y > h) p.y = h;

      const lifeRatio = p.life / p.maxLife;
      const alpha = p.baseAlpha * easeOut(lifeRatio);

      const g = p.graphic;
      g.clear();
      if (alpha > 0.005) {
        g.visible = true;
        g.rect(-p.size / 2, -p.size / 2, p.size, p.size);
        g.fill({ color: p.color, alpha });
        // Tiny glow
        g.circle(0, 0, p.size * 0.8);
        g.fill({ color: p.color, alpha: alpha * 0.2 });
      } else {
        g.visible = false;
      }
      g.x = p.x;
      g.y = p.y;
    }

    // Floor line wobble
    for (let i = 0; i < floorLines.length; i++) {
      const g = floorLines[i].graphic;
      g.clear();
      g.visible = true;
      const lineAlpha = scare ? 0.3 : 0.12;
      const yOffset = i * 2;

      // Wavy floor line
      g.moveTo(0, h - yOffset);
      const segments = 12;
      for (let s = 1; s <= segments; s++) {
        const sx = (s / segments) * w;
        const sy = h - yOffset + Math.sin(effect.elapsed * 0.005 + s * 0.8 + i) * shakeIntensity;
        g.lineTo(sx, sy);
      }
      const lineColor = COLORS.vibration_floor[i % COLORS.vibration_floor.length];
      g.stroke({ color: lineColor, alpha: lineAlpha, width: 1.5 });
    }
  }

  /**
   * Temp Controller: Cold wind particles flowing left-to-right.
   * Mix of snowflake (star) and frost (circle) particles with gentle wobble.
   */
  private updateTempController(effect: ActiveEffect, dt: number): void {
    const w = effect.roomW;
    const h = effect.roomH;
    const scare = effect.scareActive;
    const speedMul = scare ? 2.2 : 1.0;

    for (const p of effect.particles) {
      p.life -= dt * speedMul;
      if (p.life <= 0 || p.x > w + 20) {
        this.spawnTempParticle(p, w, h);
        if (scare) {
          p.vx *= 1.8;
        }
      }

      // Move rightward with vertical wobble
      p.x += p.vx * dt * 0.06 * speedMul;
      p.y += p.vy * dt * 0.06 + Math.sin(effect.elapsed * 0.002 + p.seed) * 0.15;

      // Rotate star particles
      if (p.shape === 'star') {
        p.angle += p.angularVel * dt;
      }

      drawParticle(p);
    }
  }

  /**
   * Hologram Device: Green translucent ghost silhouette with scanlines
   * and glitch effects.
   */
  private updateHologram(effect: ActiveEffect, _dt: number): void {
    const w = effect.roomW;
    const h = effect.roomH;
    const cx = w / 2;
    const scare = effect.scareActive;
    const colors = COLORS.hologram_device;

    // Fade in/out cycle
    const fadeT = (effect.elapsed % 5000) / 5000;
    const baseAlpha = Math.sin(fadeT * Math.PI) * (scare ? 0.55 : 0.3);

    // Glitch offset
    let glitchX = 0;
    if (scare || Math.random() > 0.95) {
      glitchX = (Math.random() - 0.5) * 12;
    }

    // Ghost body parts: 0=head, 1=body, 2=left arm hint, 3=right arm hint
    // Scanline particles: 4-9
    // Glitch bars: 10-11
    const headP = effect.particles[0];
    const bodyP = effect.particles[1];
    const leftArmP = effect.particles[2];
    const rightArmP = effect.particles[3];

    // -- Head --
    if (headP) {
      const g = headP.graphic;
      g.clear();
      if (baseAlpha > 0.005) {
        g.visible = true;
        const headR = Math.min(w, h) * 0.08;
        g.circle(0, 0, headR);
        g.fill({ color: colors[0], alpha: baseAlpha });
        g.circle(0, 0, headR * 1.4);
        g.fill({ color: colors[1], alpha: baseAlpha * 0.2 });
      } else {
        g.visible = false;
      }
      g.x = cx + glitchX;
      g.y = h * 0.28;
    }

    // -- Body --
    if (bodyP) {
      const g = bodyP.graphic;
      g.clear();
      if (baseAlpha > 0.005) {
        g.visible = true;
        const bw = Math.min(w, h) * 0.12;
        const bh = Math.min(w, h) * 0.22;
        g.ellipse(0, 0, bw, bh);
        g.fill({ color: colors[0], alpha: baseAlpha * 0.7 });
        // Outer glow
        g.ellipse(0, 0, bw * 1.3, bh * 1.2);
        g.fill({ color: colors[2], alpha: baseAlpha * 0.15 });
      } else {
        g.visible = false;
      }
      g.x = cx + glitchX;
      g.y = h * 0.52;
    }

    // -- Arms (subtle wisp shapes) --
    if (leftArmP) {
      const g = leftArmP.graphic;
      g.clear();
      if (baseAlpha > 0.01) {
        g.visible = true;
        const armSway = Math.sin(effect.elapsed * 0.002) * 3;
        g.ellipse(0, 0, 4, 12);
        g.fill({ color: colors[1], alpha: baseAlpha * 0.4 });
        g.x = cx - w * 0.1 + glitchX + armSway;
        g.y = h * 0.48;
      } else {
        g.visible = false;
      }
    }

    if (rightArmP) {
      const g = rightArmP.graphic;
      g.clear();
      if (baseAlpha > 0.01) {
        g.visible = true;
        const armSway = Math.sin(effect.elapsed * 0.002 + Math.PI) * 3;
        g.ellipse(0, 0, 4, 12);
        g.fill({ color: colors[1], alpha: baseAlpha * 0.4 });
        g.x = cx + w * 0.1 + glitchX + armSway;
        g.y = h * 0.48;
      } else {
        g.visible = false;
      }
    }

    // -- Scanlines (index 4-9) --
    for (let i = 4; i < Math.min(10, effect.particles.length); i++) {
      const p = effect.particles[i];
      const g = p.graphic;
      g.clear();

      const scanSpeed = scare ? 80 : 40;
      const scanY = ((effect.elapsed / scanSpeed + (i - 4) * h * 0.18) % (h * 1.2)) - h * 0.1;
      const scanAlpha = baseAlpha * 0.25;

      if (scanAlpha > 0.005) {
        g.visible = true;
        g.rect(0, scanY, w, 1.5);
        g.fill({ color: colors[1], alpha: scanAlpha });
      } else {
        g.visible = false;
      }
    }

    // -- Glitch bars (index 10-11) --
    for (let i = 10; i < Math.min(12, effect.particles.length); i++) {
      const p = effect.particles[i];
      const g = p.graphic;
      g.clear();

      const doGlitch = scare ? Math.random() > 0.3 : Math.random() > 0.92;
      if (doGlitch && baseAlpha > 0.01) {
        g.visible = true;
        const gy = Math.random() * h;
        const gw = rand(w * 0.3, w * 0.8);
        const gx = rand(-10, w - gw + 10);
        g.rect(gx, gy, gw, rand(1, 3));
        g.fill({ color: pick(colors), alpha: baseAlpha * 0.3 });
      } else {
        g.visible = false;
      }
    }
  }

  /**
   * Auto Lock: Two dark panels (doors) that slide inward.
   * Only animates during scare; otherwise stays invisible.
   */
  private updateAutoLock(effect: ActiveEffect, dt: number): void {
    const w = effect.roomW;
    const h = effect.roomH;
    const scare = effect.scareActive;
    const colors = COLORS.auto_lock;

    // Initialize door state
    if (effect.state['doorPos'] === undefined) {
      effect.state['doorPos'] = 0; // 0 = open, 1 = closed
      effect.state['doorTarget'] = 0;
      effect.state['doorVel'] = 0;
    }

    // Set target
    effect.state['doorTarget'] = scare ? 0.85 : 0;

    // Smooth spring-like animation toward target
    const target = effect.state['doorTarget'];
    const current = effect.state['doorPos'];
    const diff = target - current;

    if (scare) {
      // Slam shut fast
      effect.state['doorVel'] = diff * 0.15;
    } else {
      // Slowly open
      effect.state['doorVel'] = diff * 0.02;
    }
    effect.state['doorPos'] += effect.state['doorVel'] * dt * 0.06;
    effect.state['doorPos'] = Math.max(0, Math.min(1, effect.state['doorPos']));

    const doorProgress = effect.state['doorPos'];

    // Don't render if doors are fully open
    if (doorProgress < 0.005) {
      for (const p of effect.particles) {
        p.graphic.visible = false;
      }
      return;
    }

    const doorWidth = w * 0.45 * doorProgress;
    const alpha = 0.5 + doorProgress * 0.3;

    for (let i = 0; i < effect.particles.length; i++) {
      const g = effect.particles[i].graphic;
      g.clear();
      g.visible = true;

      const color = colors[i % colors.length];
      if (i === 0) {
        // Left door
        g.rect(0, 0, doorWidth, h);
        g.fill({ color, alpha });
        // Door edge highlight
        g.rect(doorWidth - 2, 0, 2, h);
        g.fill({ color: 0x444466, alpha: alpha * 0.5 });
      } else {
        // Right door
        g.rect(w - doorWidth, 0, doorWidth, h);
        g.fill({ color, alpha });
        // Door edge highlight
        g.rect(w - doorWidth, 0, 2, h);
        g.fill({ color: 0x444466, alpha: alpha * 0.5 });
      }
    }
  }

  /**
   * Scent Sprayer: Spiral mist particles rising from center.
   * Green/purple with sparkle accents.
   */
  private updateScentSprayer(effect: ActiveEffect, dt: number): void {
    const w = effect.roomW;
    const h = effect.roomH;
    const scare = effect.scareActive;
    const intensityMul = scare ? 1.5 : 1.0;

    for (const p of effect.particles) {
      p.life -= dt;
      if (p.life <= 0) {
        this.spawnScentParticle(p, w, h);
        if (scare) {
          p.baseAlpha *= 1.4;
          p.vy *= 1.3;
        }
      }

      const lifeRatio = p.life / p.maxLife;

      // Spiral upward motion
      p.angle += p.angularVel * dt;
      const spiralRadius = 8 + (1 - lifeRatio) * 15;
      const spiralOffsetX = Math.cos(p.angle) * spiralRadius;

      p.x += p.vx * dt * 0.05 + spiralOffsetX * 0.01;
      p.y += p.vy * dt * 0.06;

      // Grow slightly as rising
      const growFactor = 1 + (1 - lifeRatio) * 0.5;
      const currentSize = p.size * growFactor;

      const fadeAlpha = easeInOutSine(lifeRatio) * p.baseAlpha * intensityMul;

      const g = p.graphic;
      g.clear();
      if (fadeAlpha > 0.005) {
        g.visible = true;
        // Core
        g.circle(0, 0, currentSize);
        g.fill({ color: p.color, alpha: fadeAlpha });
        // Outer glow
        g.circle(0, 0, currentSize * 1.8);
        g.fill({ color: p.color, alpha: fadeAlpha * 0.15 });

        // Sparkle at random moments
        if (Math.random() > 0.92) {
          const sparkSize = currentSize * 0.3;
          g.rect(-sparkSize, -0.5, sparkSize * 2, 1);
          g.fill({ color: 0xffffff, alpha: fadeAlpha * 0.8 });
          g.rect(-0.5, -sparkSize, 1, sparkSize * 2);
          g.fill({ color: 0xffffff, alpha: fadeAlpha * 0.8 });
        }
      } else {
        g.visible = false;
      }
      g.x = p.x;
      g.y = p.y;
    }
  }
}
